// Drag-and-drop rig assignment board (turbo_logistics.md §6.1).
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "./logisticsRoutesShared";
import type { useLogisticsRoutesDispatch } from "./useLogisticsRoutesDispatch";
import type { LogiCarrierRig } from "@/lib/logistics";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  d: ReturnType<typeof useLogisticsRoutesDispatch>;
};

function formatRigCapacity(rig: LogiCarrierRig): string | null {
  if (rig.capacity_value === null || !rig.capacity_unit) return null;
  return `${rig.capacity_value} ${rig.capacity_unit.replace(/_/g, " ")}`;
}

export function LogisticsRoutesDispatchBoard({ d }: Props) {
  return (
    <div {...{ [FIREFLY_TARGET_ATTR]: "logi-dispatch-board" }}>
      <SectionCard title="Dispatch board">
      <p className="text-xs text-gray-600 dark:text-zinc-400 mb-3">
        Drag a planned delivery from the queue onto a rig to assign carrier + driver. Routes with a
        schedule flip to <strong>scheduled</strong> automatically.
      </p>

      {d.assignError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {d.assignError}
          <button type="button" className={`${ghostBtnClass} ml-2 text-[11px]`} onClick={d.clearAssignFeedback}>
            Dismiss
          </button>
        </div>
      ) : null}

      {d.assignWarnings.length > 0 ? (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
          <div className="font-semibold mb-1">Assignment warnings</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {d.assignWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
          <button type="button" className={`${ghostBtnClass} mt-2 text-[11px]`} onClick={d.clearAssignFeedback}>
            Dismiss
          </button>
        </div>
      ) : null}

      {d.rigsLoading ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading rigs…</div>
      ) : d.rigs.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          No active rigs yet. Add fleet rows under Logistics → Carriers.
        </div>
      ) : (
        <ul className="space-y-3">
          {d.rigs.map((rig) => {
            const assigned = d.routesByRig.get(rig.id) ?? [];
            const capacity = formatRigCapacity(rig);
            const busy = d.assignBusyRigId === rig.id;
            return (
              <li
                key={rig.id}
                className="rounded-xl border border-dashed border-gray-300 bg-gray-50/80 px-3 py-2 dark:border-zinc-600 dark:bg-zinc-900/40"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const routeId = e.dataTransfer.getData("text/logi-route-id");
                  if (routeId) void d.assignRoute(routeId, rig.id);
                }}
              >
                <div className="flex flex-wrap items-baseline gap-2 text-sm font-medium text-gray-900 dark:text-zinc-100">
                  <span>{rig.designation}</span>
                  {rig.standard_driver_name ? (
                    <span className="text-xs font-normal text-gray-600 dark:text-zinc-400">
                      — {rig.standard_driver_name}
                    </span>
                  ) : null}
                  {capacity ? (
                    <span className="text-[10px] font-semibold uppercase tracking-wide rounded bg-gray-200 px-1.5 py-0.5 text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
                      {capacity}
                    </span>
                  ) : (
                    <span className="text-[10px] italic text-amber-700 dark:text-amber-300">
                      no capacity on file
                    </span>
                  )}
                  {busy ? (
                    <span className="text-xs text-gray-500 dark:text-zinc-400">Assigning…</span>
                  ) : null}
                </div>
                {assigned.length > 0 ? (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {assigned.map((r) => (
                      <li
                        key={r.id}
                        className="rounded-md bg-white border border-gray-200 px-2 py-0.5 text-xs dark:bg-zinc-800 dark:border-zinc-600"
                      >
                        {d.routeChipLabel(r)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-1 text-xs italic text-gray-500 dark:text-zinc-500">
                    Drop a planned route here
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {d.plannedRoutes.length > 0 ? (
        <div className="mt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-2">
            Unassigned planned ({d.plannedRoutes.length})
          </div>
          <ul className="flex flex-wrap gap-2">
            {d.plannedRoutes.map((r) => (
              <li key={r.id}>
                <span
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/logi-route-id", r.id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  className="inline-block cursor-grab rounded-lg border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-900 active:cursor-grabbing dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100"
                  title="Drag onto a rig row"
                >
                  {d.routeChipLabel(r)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </SectionCard>
    </div>
  );
}
